/**
 * The games you starred.
 *
 * One grid, not shelves. A library is sectioned because twelve games with no
 * divisions answer no question anybody arrives with - but a list you built
 * yourself is already the answer, and dividing six chosen games into four
 * headings would be filing somebody's shortlist for them.
 *
 * Order is the order you starred them in, which `useFavourites` keeps. Sorting
 * it would throw away the only thing the list knows that the catalogue does
 * not.
 */

import { Link } from 'react-router-dom';
import { useCatalog } from '../use-catalog.js';
import { useFavourites } from '../use-favourites.js';
import { useSession } from '../session-context.js';
import { Icon } from '../render/Icon.js';
import { GameCard } from './GameCard.js';

export function Favourites() {
  const { catalog, error, retry } = useCatalog();
  const { session } = useSession();
  const favourites = useFavourites();

  if (error) {
    return (
      <div className="page narrow felt-page library-page">
        <h1 className="page-title page-heading">Favorites</h1>
        <p className="banner error">{error}</p>
        <button type="button" className="big-button" onClick={retry}>
          Try again
        </button>
      </div>
    );
  }

  if (!catalog || favourites.loading) return <div className="splash">Dealing…</div>;

  /*
   * Walked in the order they were starred, rather than filtering the
   * catalogue - which would hand them back in catalogue order and quietly
   * lose the one thing this list knows.
   */
  const games = favourites.ids
    .map((id) => catalog.games.find((game) => game.id === id))
    .filter((game): game is NonNullable<typeof game> => game !== undefined);

  return (
    <div className="page felt-page library-page">
      <h1 className="page-title page-heading">Favorites</h1>

      {session === null ? (
        <p className="page-lede">Sign in to keep a list of your favorites.</p>
      ) : games.length === 0 ? (
        <>
          <p className="page-lede">Nothing starred yet.</p>
          <p className="field-help">
            Tap the heart on any game and it will be waiting here.
          </p>
          <Link className="cta cta-primary" to="/library">
            <Icon name="gamepad" className="cta-mark" />
            <span className="cta-label">Your games</span>
            <Icon name="chevron-right" />
          </Link>
        </>
      ) : (
        <>
          <p className="page-lede">The ones you keep coming back to.</p>
          <div className="game-grid">
            {games.map((game) => (
              <GameCard
                key={game.id}
                game={game}
                starred
                onStar={() => favourites.toggle(game.id)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

